import {
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  signal,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpHeaders } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { filter, finalize, Subject, takeUntil } from 'rxjs';

import { APIService } from '@core/services';
import { PatientService } from '@core/services/patient.service';
import { ModalTypes } from '@core/enums';
import { ModalComponent, ModalOptions, TableComponent, TableOptions } from '@shared/components';

import { LabReport } from '@core/classes/tables/lab-report';
import { LabReportStatusActionComponent } from '@modules/cy/patient/orcd/components/lab-reports/actions/lab-report-status-action/lab-report-status-action.component';
import { AddLabReportComponent } from '@modules/cy/patient/orcd/components/lab-reports/actions/add-lab-report/add-lab-report.component';

@Component({
  selector: 'ncp-lab-reports',
  imports: [TableComponent, ModalComponent],
  templateUrl: './lab-reports.component.html',
  styleUrls: ['./lab-reports.component.scss'],
})
export class LabReportsComponent implements OnInit, OnDestroy {
  @ViewChild('labReports')
  public labReportsEl!: TableComponent<LabReport>;

  @ViewChild('previewMod')
  protected previewMod!: ModalComponent;

  @Output() loaded = new EventEmitter<void>();

  protected readonly router = inject(Router);
  protected apiService = inject(APIService);
  protected patientService = inject(PatientService);
  protected renderer2 = inject(Renderer2);

  private sanitizer = inject(DomSanitizer);

  private subscription: any;
  private destroy$ = new Subject<void>();
  private lastLoadedUrl = this.router.url;

  protected readonly documentType = '11502-2';

  protected optionsReady = signal<boolean>(false);
  protected pdfUrl = signal<SafeResourceUrl | null>(null);

  private objectUrl: string | null = null;

  private loadingPreviewId: number | null = null;

  private previewCells = new Map<number, HTMLTableCellElement>();

  protected previewModOptions = signal<ModalOptions>(
    new ModalOptions({
      id: 'labReportPreviewModal',
      title: 'preview_document',
      type: ModalTypes.empty,
      options: {
        animation: true,
        fullscreen: true,
        backdrop: 'static',
        keyboard: false,
      },
    })
  );

  protected get isCreateMode(): boolean {
    return this.router.url === '/cy/patient/orcd/create';
  }

  protected get ignoreColumns(): string[] {
    const common = [
      'id',
      'patient_id',
      'version_id',
      'document_type',
      'orcd_document_id',
      'author',
      'specialty',
      'reason_of_hospitalization',
      'organization_id',
      'organization_tel',
      'organization_email',
      'organization_street',
      'organization_postal_code',
      'organization_city',
      'organization_country',
    ];

    return this.isCreateMode ? [...common, 'preview'] : [...common, 'status'];
  }

  protected options = signal<TableOptions<LabReport>>(
    new TableOptions<LabReport>({
      id: 'lab_reports',
      type: 'LabReports',
      interface: LabReport,
      data: [],
      autoLoadData: false,
      ignoreColumns: this.ignoreColumns,
      pagination: {
        itemsPerPage: 10,
        pageNumber: 1,
        totalItems: 0,
      },
      searchComponent: null,
    })
  );

  ngOnInit(): void {
    this.getData(this.isCreateMode ? -1 : 1, this.documentType);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        const currentUrl = event.urlAfterRedirects;

        if (currentUrl === this.lastLoadedUrl) {
          return;
        }

        this.lastLoadedUrl = currentUrl;

        if (this.subscription) {
          this.subscription.unsubscribe();
          this.subscription = null;
        }

        this.optionsReady.set(false);

        this.loadingPreviewId = null;
        this.previewCells.clear();

        this.closePreview();

        this.getData(this.isCreateMode ? -1 : 1, this.documentType);
      });
  }

  protected getData = (status: number = 1, documentType: string = this.documentType): void => {
    this.subscription = this.apiService
      .call({
        path: '/get-documents-list',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          status,
          document_type: documentType,
        },
      })
      ?.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response && response.status === 1) {
            const data = (response.data || [])
              .filter((item: LabReport) => {
                return this.isCreateMode || item.status === 1;
              })
              .map((item: LabReport) => ({
                ...item,
                preview: '',
              }));

            this.previewCells.clear();

            this.options.set(
              new TableOptions<LabReport>({
                id: 'lab_reports',
                type: 'LabReports',
                interface: LabReport,
                data,
                autoLoadData: false,
                ignoreColumns: this.ignoreColumns,

                actionsComponent: this.isCreateMode ? LabReportStatusActionComponent : undefined,

                extraComponent: this.isCreateMode ? AddLabReportComponent : undefined,

                pagination: {
                  itemsPerPage: 10,
                  pageNumber: 1,
                  totalItems: data.length,
                },

                renderCustomRow: (td: HTMLTableCellElement, row: {}, item: string) => {
                  const data = row as LabReport;

                  if (item === 'preview') {
                    const documentId = this.getDocumentId(data);

                    this.previewCells.set(documentId, td);

                    this.renderPreviewCell(td, data);
                  }

                  if (item === 'status') {
                    td.innerHTML = '';

                    const iEl = this.renderer2.createElement('h2');

                    if (data.status === 1) {
                      this.renderer2.addClass(iEl, 'bi');
                      this.renderer2.addClass(iEl, 'bi-eye');
                      this.renderer2.addClass(iEl, 'text-primary');
                    } else {
                      this.renderer2.addClass(iEl, 'bi');
                      this.renderer2.addClass(iEl, 'bi-eye-slash');
                      this.renderer2.addClass(iEl, 'text-secondary');
                    }

                    this.renderer2.appendChild(td, iEl);
                  }
                },

                searchComponent: null,
              })
            );
          }

          this.optionsReady.set(true);
          this.loaded.emit();
        },

        error: () => {
          this.optionsReady.set(true);
          this.loaded.emit();
        },
      });
  };

  private renderPreviewCell(td: HTMLTableCellElement, data: LabReport): void {
    td.innerHTML = '';

    const documentId = this.getDocumentId(data);

    if (this.loadingPreviewId === documentId) {
      const spinner = this.renderer2.createElement('span');

      this.renderer2.addClass(spinner, 'spinner-border');

      this.renderer2.addClass(spinner, 'spinner-border-sm');

      this.renderer2.addClass(spinner, 'text-primary');

      this.renderer2.setAttribute(spinner, 'role', 'status');

      this.renderer2.setAttribute(spinner, 'aria-hidden', 'true');

      this.renderer2.appendChild(td, spinner);

      return;
    }

    const iEl = this.renderer2.createElement('h2');

    this.renderer2.addClass(iEl, 'bi');
    this.renderer2.addClass(iEl, 'bi-file-earmark-pdf');
    this.renderer2.addClass(iEl, 'text-primary');

    this.renderer2.setStyle(iEl, 'cursor', 'pointer');

    this.renderer2.listen(iEl, 'click', (event: Event) => {
      event.stopPropagation();

      this.previewDoc(data);
    });

    this.renderer2.appendChild(td, iEl);
  }

  private getDocumentId(item: LabReport): number {
    return Number(item.orcd_document_id || item.id);
  }

  protected previewDoc = (item: LabReport): void => {
    const documentId = this.getDocumentId(item);

    if (this.loadingPreviewId !== null) {
      return;
    }

    const payload = {
      document_type: item.document_type || this.documentType,

      orcd_document_id: documentId,
    };

    this.closePreview();

    this.loadingPreviewId = documentId;

    const cell = this.previewCells.get(documentId);

    if (cell) {
      this.renderPreviewCell(cell, item);
    }

    this.apiService
      .call({
        path: '/get-orcd-document',

        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),

        body: payload,
      })
      ?.pipe(
        finalize(() => {
          this.loadingPreviewId = null;

          const currentCell = this.previewCells.get(documentId);

          if (currentCell) {
            this.renderPreviewCell(currentCell, item);
          }
        })
      )
      .subscribe({
        next: (response: any) => {
          const selectedDocument = response?.data || [];

          const doc = selectedDocument[0];

          const base64 = doc?.document || doc?.documentContent || doc?.pdfContent || doc?.content;

          if (!base64) {
            console.error('No PDF document returned:', response);

            return;
          }

          try {
            const cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;

            const byteChars = atob(cleanBase64);

            const byteNumbers = new Array(byteChars.length);

            for (let i = 0; i < byteChars.length; i++) {
              byteNumbers[i] = byteChars.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);

            const blob = new Blob([byteArray], {
              type: 'application/pdf',
            });

            this.objectUrl = URL.createObjectURL(blob);

            this.pdfUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl));

            this.previewMod.open();
          } catch (error) {
            console.error('Unable to preview lab report PDF:', error);
          }
        },

        error: (error) => {
          console.error('Error loading lab report PDF:', error);
        },
      });
  };

  protected closePreview = (): void => {
    this.pdfUrl.set(null);

    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);

      this.objectUrl = null;
    }
  };

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.previewCells.clear();
    this.loadingPreviewId = null;

    this.closePreview();

    this.destroy$.next();
    this.destroy$.complete();
  }
}
